import * as React from "react";
import { copyIcon } from "@assets/images";

interface ClickToCopyProps {
  height?: string;
  nodeToCopy?: HTMLElement | null;
  notificationCallback?: () => void;
  imgStyle?: string;
  textToCopy?: string;
}

const ClickToCopy: React.FC<ClickToCopyProps> = ({
  height = "h-5",
  nodeToCopy = null,
  notificationCallback = () => {},
  imgStyle = "",
  textToCopy,
}) => {
  const copyTextToClipboard = (text: string): void => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).catch(() => {
          fallbackCopyText(text);
        });
      } else {
        fallbackCopyText(text);
      }
    } catch (err) {
      console.warn(err);
    }
    notificationCallback();
  };

  const fallbackCopyText = (text: string): void => {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
  };

  const copyToClipboard = (node: HTMLElement | null) => (
    e: React.MouseEvent<HTMLImageElement>
  ): void => {
    if (textToCopy !== undefined) {
      copyTextToClipboard(textToCopy);
      return;
    }
    const nodeToCopy = node;
    if (nodeToCopy) {
      try {
        const microsoftBrowser = (document as any).selection;
        const otherBrowsers = window.getSelection();
        
        if (
          nodeToCopy.tagName !== "INPUT" &&
          nodeToCopy.tagName !== "TEXTAREA"
        ) {
          let range: any;
          if (microsoftBrowser) {
            // off chance someone still uses IE
            range = (document.body as any).createTextRange();
            range.moveToElementText(nodeToCopy);
            range.select();
          } else if (otherBrowsers) {
            range = document.createRange();
            range.selectNode(nodeToCopy);
            otherBrowsers.removeAllRanges();
            otherBrowsers.addRange(range);
          }
        } else {
          (nodeToCopy as HTMLInputElement).focus();
        }
        
        document.execCommand("copy");
        
        if (microsoftBrowser) {
          (document as any).selection.empty();
        } else if (otherBrowsers) {
          otherBrowsers.removeAllRanges();
        }
      } catch (err) {
        console.warn(err);
      }
      notificationCallback();
    }
  };

  if (!textToCopy && !document.queryCommandSupported("copy")) {
    return null;
  }

  return (
    <img
      data-testid="button-copy"
      className={`${height} cursor-pointer ${imgStyle}`}
      src={copyIcon}
      onClick={copyToClipboard(nodeToCopy)}
      alt="Copy to clipboard"
    />
  );
};

export default ClickToCopy;
